import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Player {
  id: string;
  name: string;
}

interface ChipEntry {
  player_id: string;
  final_chips: number;
}

interface Buyin {
  player_id: string;
  amount: number;
  chips: number;
}

interface ChipEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  players: Player[];
  existingEntries: ChipEntry[];
  onChipEntryAdded: () => void;
  gameStatus?: string;
  buyins?: Buyin[];
  selectedPlayerId?: string;
}

export const ChipEntryDialog = ({ open, onOpenChange, gameId, players, existingEntries, onChipEntryAdded, gameStatus, buyins = [], selectedPlayerId: initialSelectedPlayerId }: ChipEntryDialogProps) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState(initialSelectedPlayerId || "");
  const [finalChips, setFinalChips] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // For editing mode, show all players who are playing, not just those without entries
  const playingPlayerIds = [...new Set(buyins.map(buyin => buyin.player_id))];
  const playingPlayers = players.filter(player => playingPlayerIds.includes(player.id));
  
  const availablePlayers = gameStatus === 'settled' || initialSelectedPlayerId 
    ? playingPlayers 
    : players.filter(player => !existingEntries.some(entry => entry.player_id === player.id));
  
  const isGameSettled = gameStatus === 'settled';

  // Calculate total chips bought and chips remaining for selected player
  const selectedPlayerBuyins = buyins.filter(b => b.player_id === selectedPlayerId);
  const totalChipsBought = selectedPlayerBuyins.reduce((sum, buyin) => sum + buyin.chips, 0);
  const totalChipsOnTable = existingEntries.reduce((sum, entry) => sum + entry.final_chips, 0);
  const allPlayersBuyins = buyins.reduce((sum, buyin) => sum + buyin.chips, 0);
  const chipsLeftToCheckout = allPlayersBuyins - totalChipsOnTable;

  // Load existing chip entry when editing
  useEffect(() => {
    if (initialSelectedPlayerId) {
      setSelectedPlayerId(initialSelectedPlayerId);
      const existingEntry = existingEntries.find(entry => entry.player_id === initialSelectedPlayerId);
      if (existingEntry) {
        setFinalChips(existingEntry.final_chips.toString());
      }
    }
  }, [initialSelectedPlayerId, existingEntries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlayerId || !finalChips) return;

    const chips = parseInt(finalChips);
    if (chips < 0) {
      toast({
        title: "Error",
        description: "Chip count cannot be negative",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Check if entry already exists
      const existingEntry = existingEntries.find(entry => entry.player_id === selectedPlayerId);
      
      let result;
      if (existingEntry) {
        // Update existing entry
        result = await supabase
          .from('chip_entries')
          .update({ final_chips: chips })
          .eq('player_id', selectedPlayerId)
          .eq('game_id', gameId);
      } else {
        // Insert new entry
        result = await supabase
          .from('chip_entries')
          .insert({ 
            player_id: selectedPlayerId,
            game_id: gameId,
            final_chips: chips
          });
      }

      if (result.error) throw result.error;

      toast({
        title: "Success",
        description: "Chip count updated successfully"
      });

      setSelectedPlayerId("");
      setFinalChips("");
      onOpenChange(false);
      onChipEntryAdded();
    } catch (error) {
      console.error('Chip entry error:', error);
      toast({
        title: "Error",
        description: `Failed to record chip count: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isGameSettled ? 'Final Chip Counts' : 'Enter Final Chip Count'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="player">Player</Label>
            <Select 
              value={selectedPlayerId} 
              onValueChange={setSelectedPlayerId} 
              disabled={isGameSettled || !!initialSelectedPlayerId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a player" />
              </SelectTrigger>
              <SelectContent>
                {availablePlayers.map((player) => (
                  <SelectItem key={player.id} value={player.id}>
                    {player.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="finalChips">
              Final Chip Count
              {selectedPlayerId && (
                <span className="text-sm text-muted-foreground ml-2">
                  / {chipsLeftToCheckout.toLocaleString()} (total left to check out)
                </span>
              )}
            </Label>
            <Input
              id="finalChips"
              type="number"
              min="0"
              value={finalChips}
              onChange={(e) => setFinalChips(e.target.value)}
              placeholder="Enter final chip count"
              required
              disabled={isGameSettled}
            />
          </div>

          {availablePlayers.length === 0 && (
            <p className="text-sm text-muted-foreground">
              All players have already entered their chip counts.
            </p>
          )}

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {isGameSettled ? 'Close' : 'Cancel'}
            </Button>
            {!isGameSettled && (
              <Button type="submit" disabled={loading || !selectedPlayerId || !finalChips || availablePlayers.length === 0}>
                {loading ? "Recording..." : "Record Chips"}
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};